/**
 * #976 R2: the READ-ONLY trader-notes layer of TraderDetail. A manager or admin sees the notes a
 * trader in their own org wrote on their cards. This file has no write path: the only call it makes
 * is `client.readTraderNotes`, and the client has no write counterpart for it.
 *
 * With no `transport` (`TraderDetailRoute` passes the browser singleton; a route that passes none) the hook
 * sends nothing and returns an empty map.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { cardTargetKey, createCardAnnotationClient } from '@/lib/cardAnnotations/client';
import { cardTargetIdentityValid } from '@/lib/cardAnnotations/identity';
import { TRADER_VIEW_SCOPE, type CardAnnotationTransport, type CardTarget } from '@/lib/cardAnnotations/types';
import type { CardNoteRecord } from '@/lib/cardNotes/types';

/** The server's batch limit. */
const CHUNK = 50;
const NO_NOTES: ReadonlyMap<string, CardNoteRecord> = new Map();

export const kalshiOrderNoteTarget = (orderId: unknown): CardTarget | null =>
  typeof orderId === 'string' && orderId !== '' ? { kind: 'order', scope: TRADER_VIEW_SCOPE, venue: 'kalshi', venueOrderId: orderId } : null;
/** The CLOB order id of a Polymarket open order: the same expression as `buildPolyOrderVM(order).cancelId` (pinned in a test), without building the card. */
export const polyOrderIdOf = (order: { id?: unknown; orderID?: unknown; orderId?: unknown }): string =>
  String(order.id ?? order.orderID ?? order.orderId ?? '');
export const polyOrderNoteTarget = (order: { id?: unknown; orderID?: unknown; orderId?: unknown }): CardTarget | null => {
  const orderId = polyOrderIdOf(order);
  return orderId !== '' ? { kind: 'order', scope: TRADER_VIEW_SCOPE, venue: 'polymarket', venueOrderId: orderId } : null;
};
export const polyPositionNoteTarget = (marketId: string, outcome: 'yes' | 'no'): CardTarget =>
  ({ kind: 'position', scope: TRADER_VIEW_SCOPE, venue: 'polymarket', marketId, outcome });

/**
 * Notes by `cardTargetKey`, for the targets on the page. Pass a STABLE transport (a module singleton such as
 * `browserCardAnnotationTransport`): a new object per render builds a new client and re-reads. Read once per distinct target set, and again
 * when the page finishes a refresh (`ready` goes false then true). No retry, no polling; a failed
 * read shows no notes. A reply for an older read, an older target set or an unmounted page is dropped.
 */
export function useTraderNotes(opts: {
  transport: CardAnnotationTransport | undefined;
  traderId: string;
  targets: readonly CardTarget[];
  ready: boolean;
}): ReadonlyMap<string, CardNoteRecord> {
  const { transport, traderId, targets, ready } = opts;
  const client = useMemo(() => (transport ? createCardAnnotationClient(transport) : null), [transport]);
  const targetsKey = useMemo(() => JSON.stringify(targets.map(cardTargetKey).sort()), [targets]);
  const [notes, setNotes] = useState<ReadonlyMap<string, CardNoteRecord>>(NO_NOTES);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    // Nothing to read (dormant, mid-refresh, or no rows): leave the map as it is. Rows that left the
    // page take their badge with them, and a different trader remounts the page.
    // A target the server would refuse by identity (e.g. an empty Polymarket market id) never rides in
    // a batch: one bad id fails the whole read and would hide every note on the page. It gets no badge.
    const valid = targets.filter(cardTargetIdentityValid);
    if (!client || !ready || valid.length === 0) return () => { seq.current += 1; };
    void (async () => {
      const next = new Map<string, CardNoteRecord>();
      try {
        for (let i = 0; i < valid.length; i += CHUNK) {
          const { records } = await client.readTraderNotes(traderId, valid.slice(i, i + CHUNK));
          if (mine !== seq.current) return;
          for (const r of records) if (r.note) next.set(cardTargetKey(r.target), r);
        }
      } catch {
        if (mine !== seq.current) return;
        setNotes(NO_NOTES);
        return;
      }
      if (mine === seq.current) setNotes(next);
    })();
    return () => { seq.current += 1; };
    // `targets` is covered by `targetsKey`; a new array with the same keys must not re-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, traderId, targetsKey, ready]);

  return client ? notes : NO_NOTES;
}
